import { gql } from '@apollo/client';
import Dropdown from '@orn/shared-web/components/Dropdown';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { CalendarIcon, EditIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { DateRangePicker, DateRangePickerModal, type CalendarDateRange } from '../../DateRangePicker';
import Entity from '../../entity';
import UpdateMerchantModal from '../../UpdateMerchantModal';

import type { MerchantPaneHeaderFragment } from './__generated__/MerchantPaneHeader.gql';

interface IMerchantPaneHeaderProps {
  merchant: MerchantPaneHeaderFragment;
  dateRange?: CalendarDateRange;
  onDateRangeChange: (dateRange: CalendarDateRange) => void;
}

const MerchantPaneHeaderFragment = gql`
  fragment MerchantPaneHeader on Merchant {
    id
    ...EntityAvatar
  }
  ${Entity.Avatar.Fragment}
`;

/**
 * Pane header for a single merchant. Renders the merchant avatar (circular,
 * `md`/40px at all breakpoints so the title line matches its sibling pane
 * headers), name and trailing actions (date range, edit menu). At `<md` the
 * inline date range picker is hidden (still reachable via the dots-menu) —
 * matches the Figma Merchant "Section Header" layout.
 */
const MerchantPaneHeader = ({ merchant, dateRange, onDateRangeChange }: IMerchantPaneHeaderProps) => {
  const isMdOrUp = useBreakpoint('md');
  const [isUpdateModalOpen, setIsUpdateModalOpen] = React.useState(false);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = React.useState(false);

  return (
    <>
      <Entity.Header
        title={merchant.name}
        avatar={<Entity.Avatar entity={merchant} size="md" />}
        trailingContent={
          <div className="flex items-center gap-3">
            {isMdOrUp && <DateRangePicker value={dateRange} onChange={onDateRangeChange} defaultValue="past6Months" />}
            <Dropdown.Root>
              <Dropdown.DotsButton className="p-2" />
              <Dropdown.Popover className="w-min">
                <Dropdown.Menu>
                  {!isMdOrUp && (
                    <Dropdown.Item icon={CalendarIcon} onAction={() => setIsDateRangeModalOpen(true)}>
                      Time range&hellip;
                    </Dropdown.Item>
                  )}
                  <Dropdown.Item icon={EditIcon} onAction={() => setIsUpdateModalOpen(true)}>
                    Edit merchant&hellip;
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          </div>
        }
      />
      <UpdateMerchantModal merchant={merchant} isOpen={isUpdateModalOpen} onOpenChange={setIsUpdateModalOpen} />
      {!isMdOrUp && (
        <DateRangePickerModal
          isOpen={isDateRangeModalOpen}
          onOpenChange={setIsDateRangeModalOpen}
          value={dateRange}
          onChange={onDateRangeChange}
          defaultValue="past6Months"
        />
      )}
    </>
  );
};

MerchantPaneHeader.Fragment = MerchantPaneHeaderFragment;

export default MerchantPaneHeader;
